<template>
  <node-view-wrapper as="span" class="partner-image-wrapper" style="display: inline-block">
    <img
      v-if="resolvedSrc"
      :src="resolvedSrc"
      :alt="node.attrs.alt || ''"
      :title="node.attrs.title || ''"
      class="partner-image"
      draggable="false"
    />
    <span v-else-if="isLoading" class="partner-image-placeholder">
      <v-progress-circular indeterminate size="24" width="2" />
    </span>
    <span v-else class="partner-image-placeholder partner-image-placeholder--error">
      <v-icon small>mdi-image-broken-variant</v-icon>
    </span>
  </node-view-wrapper>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import { NodeViewWrapper, nodeViewProps } from "@tiptap/vue-2";
import { PartnerImageModule } from "@/store/modules/partner-image.store";
import { PartnerModule } from "@/store/modules/partner";

const PARTNER_IMAGE_PREFIX = "partner-image:";

@Component({
  components: { NodeViewWrapper },
  props: nodeViewProps
})
export default class PartnerImageNodeView extends Vue {
  declare node: any;

  resolvedSrc: string | null = null;
  isLoading = false;

  get src(): string {
    return this.node.attrs.src || "";
  }

  get isPartnerImage(): boolean {
    return this.src.startsWith(PARTNER_IMAGE_PREFIX);
  }

  get imageId(): string {
    return this.src.replace(PARTNER_IMAGE_PREFIX, "");
  }

  async mounted() {
    if (this.isPartnerImage) {
      await this.resolvePresignedUrl();
    } else {
      this.resolvedSrc = this.src;
    }
  }

  @Watch("node.attrs.src")
  onSrcChange() {
    this.resolvedSrc = null;
    if (this.isPartnerImage) {
      this.resolvePresignedUrl();
    } else {
      this.resolvedSrc = this.src;
    }
  }

  async resolvePresignedUrl() {
    this.isLoading = true;
    try {
      const partnerId = PartnerModule.partner._id || PartnerModule.partner.id;
      if (!partnerId) {
        this.resolvedSrc = null;
        return;
      }

      const image = await PartnerImageModule.fetchOne({ partnerId, id: this.imageId });
      this.resolvedSrc = image.url;
    } catch (error) {
      this.$log.error("Failed to resolve partner image", error);
      this.resolvedSrc = null;
    } finally {
      this.isLoading = false;
    }
  }
}
</script>

<style scoped>
.partner-image {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  cursor: default;
}

.partner-image-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 60px;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

.partner-image-placeholder--error {
  background-color: rgba(0, 0, 0, 0.08);
}
</style>
